import { useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";

export default function ContactPageForm({ config, slug, primary }) {
  const form = config?.contact_page_form;
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  if (!config?.contact_page_form_active || !form?.fields?.length) return null;

  const setValue = (id, value) => setAnswers((prev) => ({ ...prev, [id]: value }));
  const toggle = (id, label) => {
    const cur = Array.isArray(answers[id]) ? answers[id] : [];
    setValue(id, cur.includes(label) ? cur.filter((x) => x !== label) : [...cur, label]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/store/${slug}/contact-form`, { answers });
      setDone(data.message || "Thank you.");
      setAnswers({});
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-8 mx-auto max-w-xl text-left rounded-3xl border border-stone-200 bg-white p-4 sm:p-6 shadow-sm" data-testid="contact-page-form">
      <h2 className="font-display text-xl sm:text-2xl font-bold text-stone-900">{form.title || "Send a message"}</h2>
      {done ? (
        <p className="mt-4 text-sm text-emerald-700" data-testid="contact-page-form-done">{done}</p>
      ) : (
        <div className="mt-4 space-y-4">
          {form.fields.map((field) => (
            <label key={field.id} className="block text-sm">
              <span className="font-semibold text-stone-800">{field.label}{field.required ? " *" : ""}</span>
              {field.type === "text" ? (
                <input
                  className="mt-1 w-full h-11 rounded-xl border border-stone-200 px-3"
                  placeholder={field.placeholder || ""}
                  value={answers[field.id] || ""}
                  required={Boolean(field.required)}
                  data-testid={`contact-field-${field.id}`}
                  onChange={(e) => setValue(field.id, e.target.value)}
                />
              ) : null}
              {field.type === "dropdown" ? (
                <select
                  className="mt-1 w-full h-11 rounded-xl border border-stone-200 px-3 bg-white"
                  value={answers[field.id] || ""}
                  required={Boolean(field.required)}
                  data-testid={`contact-field-${field.id}`}
                  onChange={(e) => setValue(field.id, e.target.value)}
                >
                  <option value="">Select</option>
                  {(field.options || []).map((opt) => <option key={opt.id} value={opt.label}>{opt.label}</option>)}
                </select>
              ) : null}
              {field.type === "multiselect" ? (
                <div className="mt-2 space-y-2" data-testid={`contact-field-${field.id}`}>
                  {(field.options || []).map((opt) => (
                    <label key={opt.id} className="flex items-start gap-2 text-stone-700">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={(answers[field.id] || []).includes(opt.label)}
                        onChange={() => toggle(field.id, opt.label)}
                      />
                      <span className="break-words">{opt.label}</span>
                    </label>
                  ))}
                </div>
              ) : null}
            </label>
          ))}
          <button type="submit" disabled={busy} className="w-full h-11 rounded-xl text-white font-bold disabled:opacity-60" style={{ background: primary }} data-testid="contact-page-form-submit">
            {busy ? "Sending…" : (form.button_label || "Submit")}
          </button>
        </div>
      )}
    </form>
  );
}
